import clsx from 'clsx';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import type {
  ClosedMessageDocument,
  OpenMessageDocument,
  RecordingMessageDocument,
} from '@hubertai/screening-question-types';
import { EMessageSender, EQuestionType } from '@hubertai/screening-question-types';
import { useScoreInterview } from '../scoring.context';
import CandidateClosedAnswer from './CandidateClosedAnswer';
import CandidateOpenAnswer from './CandidateOpenAnswer';
import CandidateRecordingAnswer from './CandidateClosedAnswer/CandidateRecordingAnswer';
import ConditionedQuestionText from './ConditionedQuestionText';
import HubertMessage from './HubertMessage';
import TimeStamp from './TimeStamp';
import type { TChatCollection, TInterviewScoreTableDetail } from 'types/scores.types';
import IsAI from 'components/IsAi';
import { HubertBadge } from 'components/HubertUI/HubertBadge';

interface ChatCollectionProps extends ComponentProps<'div'> {
  collection: TChatCollection;
}

const ChatCollection = ({ collection, ...props }: ChatCollectionProps) => {
  const [detail, setDetail] = useState<TInterviewScoreTableDetail | undefined>();
  const { scoreTableDetails } = useScoreInterview();

  useEffect(() => {
    if (collection && scoreTableDetails && scoreTableDetails.length) {
      const d = scoreTableDetails.find((x) => x.questionId === collection.questionId);
      if (d) {
        setDetail(d);
      }
    }
  }, [scoreTableDetails, collection]);

  return (
    <div
      {...props}
      className={clsx(
        'flex flex-col justify-between gap-2',
        'bg-primary-50',
        // ' hover:bg-gray-50',
        'rounded-md',
        'p-2',
      )}
      id={`chat-collection-${collection.questionPathId}`}
    >
      {collection.question ? (
        <div className={clsx('flex flex-row justify-between gap-2', 'm-2')}>
          <div className={clsx('flex flex-row justify-start gap-2')}>
            <HubertBadge className="select-all" variant={'secondary'}>
              {collection.question.type}
            </HubertBadge>

            <HubertBadge className="select-all" variant={'outline'}>
              {collection.question.id}
            </HubertBadge>
            <IsAI aiDetection={detail && detail.aiDetection ? detail.aiDetection : undefined} />
          </div>

          <div>
            <TimeStamp
              date={collection.messages && collection.messages.length ? collection.messages[0].created_at : undefined}
            />
          </div>
          {/* <CollectionEvaluation collection={collection} /> */}
        </div>
      ) : null}

      <ConditionedQuestionText
        condition={collection.messages.some((x) => x.reusage && x.reusage.is_reused)}
        collection={collection}
      />

      {collection.messages.map((message, idx: number) => {
        if (message.sender === EMessageSender.Hubert) {
          return <HubertMessage key={idx} question={collection.question} message={message} />;
        } else if (message.sender === EMessageSender.Candidate) {
          if (!collection.question) {
            return <div key={idx}>Missing question</div>;
          }

          if (collection.question.type === EQuestionType.Open) {
            return <CandidateOpenAnswer key={idx} answer={message as OpenMessageDocument} />;
          } else if (
            collection.question.type === EQuestionType.Audio ||
            collection.question.type === EQuestionType.Video
          ) {
            // Recording answers live in open_answers with their real audio/video type.
            return (
              <CandidateRecordingAnswer
                key={idx}
                collection={collection}
                answer={message as RecordingMessageDocument}
              />
            );
          } else {
            return (
              <CandidateClosedAnswer key={idx} collection={collection} answer={message as ClosedMessageDocument} />
            );
          }
        } else {
          return (
            <div key={idx} className="bg-danger-50 text-danger-500">
              Erroneous sender type!
            </div>
          );
        }
      })}
    </div>
  );
};

export default ChatCollection;
